import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { importWithReload } from '@/lib/chunkReload';

import zhTWCommon from './locales/zh-TW/common.json';
import zhTWLogin from './locales/zh-TW/login.json';
import zhTWHome from './locales/zh-TW/home.json';
import zhTWCore from './locales/zh-TW/core.json';
import zhTWUploads from './locales/zh-TW/uploads.json';
import zhTWProjectCreate from './locales/zh-TW/projectCreate.json';
import zhTWBrandKits from './locales/zh-TW/brandKits.json';
import zhTWProjects from './locales/zh-TW/projects.json';
import zhTWJobs from './locales/zh-TW/jobs.json';
import zhTWAssets from './locales/zh-TW/assets.json';
import zhTWAds from './locales/zh-TW/ads.json';
// R45 Beta v0.0.1 — 對齊 8-agent panel 物理拆檔 namespace 隔離(groups / library;
// R281 起 `auth` 整個 namespace 隨其唯一消費者 `features/auth/errorMap` 一起退場)
import zhTWGroups from './locales/zh-TW/groups.json';
import zhTWLibrary from './locales/zh-TW/library.json';
// R209 — 素材內容檢查(檢查報告頁 / 批次進度 / minibar)namespace
import zhTWCheck from './locales/zh-TW/check.json';
// R48 D3 Tier 5 — M-10 上字編輯器 namespace
// M-15 / M-16 / M-17 / M-18 — D3 (2) 工作區設定 / 個人資料 / 訂閱與用量 / 說明
import zhTWWorkspace from './locales/zh-TW/workspace.json';
import zhTWProfile from './locales/zh-TW/profile.json';
import zhTWHelp from './locales/zh-TW/help.json';
import zhTWContracts from './locales/zh-TW/contracts.json';
// R265 — 問題回報中心(user)/ 回報管理主控台(admin)namespace
import zhTWFeedback from './locales/zh-TW/feedback.json';
// R276 — 序號兌換點數(使用者兌換 dialog / admin 序號管理)namespace
import zhTWRedeem from './locales/zh-TW/redeem.json';
// R392 — 推薦碼 × 業務歸屬 × 月結分潤(admin 管理頁)namespace
import zhTWReferral from './locales/zh-TW/referral.json';
// R316 — 公開頁(官網首頁 / 服務條款 / 免責聲明)namespace。⚠️ 三語 key 齊,但
// **ja / en 目前是未審校初稿**(文案稿只有繁中),語言選單刻意關著,見 `pages/public`。
import zhTWSite from './locales/zh-TW/site.json';
// R352 — 剪輯頁 namespace(三語 parity 由 `cutCompleteness.test.ts` 鎖)
import zhTWCut from './locales/zh-TW/cut.json';
// M-20 — AI 分析洞察 namespace
// M-23 — 文字生成(獨立全頁)namespace

/**
 * i18n 初始化
 * -----------------------------------------------------------------------------
 * - 預設語系：`zh-TW`(本專案主要受眾為繁中使用者;Phase 0 拍板)
 * - 支援語系:zh-TW / en / ja(Phase 0 起 — 大頭貼下拉切換)
 * - Namespace 按「畫面 / 跨畫面 feature」切:common / login / home / core / uploads /
 *   projectCreate / brandKits / projects / jobs / assets / ads / auth / groups /
 *   library / workspace / profile / help / contracts / feedback(共 19 個;R164 大改版刪
 *   subtitle / abcd / insights / textgen 四個 namespace)
 * - 偵測順序:localStorage → navigator → htmlTag(無手動 override 時才偵測)
 * - 持久化:`localStorage.hai.lang`(對齊既有 caches: ['localStorage'] 設定);
 *   ThemeMenu LanguageMenu 切換時 i18n.changeLanguage(lang) 自動寫進 localStorage
 *
 * @remarks
 * 本模組為 side-effect import:在 `main.tsx` 只需 `import '@/i18n'` 即完成初始化。
 *
 * **Phase 0 i18n 約定**:
 *   1. `userMenu.language.*` + `topNav.*` + `nav.tabs.*` 三組 key 在 en/ja 都已填好實際翻譯
 *   2. **其餘 namespace 在 en/ja 仍是 zh-TW starter 文本** — 9 個 Phase 1 agent 在改造
 *      各自 screen 時補對應翻譯;若某 agent 動到 `home.json` 的 key,該 agent 負責
 *      把對應 en + ja 三語都填好(同 commit 內)
 *   3. 新加 namespace 時:locale dir 同步加(zh-TW / en / ja)+ 註冊 + `resources.d.ts` 加 type
 */
const resources = {
  'zh-TW': {
    common: zhTWCommon,
    login: zhTWLogin,
    home: zhTWHome,
    core: zhTWCore,
    uploads: zhTWUploads,
    projectCreate: zhTWProjectCreate,
    brandKits: zhTWBrandKits,
    projects: zhTWProjects,
    jobs: zhTWJobs,
    assets: zhTWAssets,
    ads: zhTWAds,
    groups: zhTWGroups,
    library: zhTWLibrary,
    check: zhTWCheck,
    workspace: zhTWWorkspace,
    profile: zhTWProfile,
    help: zhTWHelp,
    contracts: zhTWContracts,
    feedback: zhTWFeedback,
    redeem: zhTWRedeem,
    referral: zhTWReferral,
    site: zhTWSite,
    cut: zhTWCut,
  },
} as const;

/**
 * R413 — en / ja 字典改成**用到才載入**(合計 ≈ 585 KB,原本每個訪客都在主 bundle 裡下載、解析)。
 * zh-TW 是預設語言也是 fallback,維持靜態;en / ja 各自一個 chunk(`resources.en.ts` /
 * `resources.ja.ts`)。
 *
 * 三條路徑都保證「畫面出現之前字典已在」,不會先閃一下繁中:
 *   1. **開機**:偵測到 en / ja → `i18nReady` 先載字典再 resolve;`main.tsx` 等它才 render
 *   2. **切語言**:一律走 `changeAppLanguage`(先載字典、再 `changeLanguage`)
 *   3. 字典載入失敗(離線 / 部署換版 chunk 404)→ 照常顯示,文字退回 fallback 繁中(console 記一筆)
 *
 * ⚠️ 不要直接呼 `i18n.changeLanguage('en' | 'ja')` 而沒先 `ensureLanguageLoaded` —— 會顯示繁中
 * 而且 `resolvedLanguage` 停在 zh-TW(語言選單的選中態跟著錯),零錯誤徵兆。
 * ⚠️ 不要從主 bundle 走得到的檔案靜態 import en / ja 的 JSON(`app/mainBundleBoundary.test.ts`)。
 */
const LAZY_LANGUAGE_LOADERS = {
  en: () => import('./resources.en'),
  ja: () => import('./resources.ja'),
} as const;
type LazyLanguage = keyof typeof LAZY_LANGUAGE_LOADERS;

/** `en-US` / `ja-JP` / `en` → 要載入的字典;zh-TW 或不支援的語言 → null(不用載)。 */
export function lazyLanguageOf(lng: string | undefined | null): LazyLanguage | null {
  if (!lng) return null;
  const base = lng.toLowerCase().split('-')[0];
  return base === 'en' || base === 'ja' ? base : null;
}

/**
 * `import.meta.glob('./locales/<lng>/*.json')` 的結果(路徑 → JSON)→ `{ <namespace>: json }`
 * (檔名即 namespace)。en / ja 字典用 glob 取代兩份各 23 行的手抄匯入清單:新增 namespace
 * 只要放 JSON 檔;三語是否一致由 `lazyLanguages.test` 對 zh-TW 的 `ns` 鎖住。
 */
export function collectNamespaces(
  mods: Record<string, Record<string, unknown>>,
): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {};
  for (const [path, data] of Object.entries(mods)) {
    const file = path.slice(path.lastIndexOf('/') + 1);
    out[file.replace(/\.json$/, '')] = data;
  }
  return out;
}

const pendingLoads = new Map<LazyLanguage, Promise<void>>();

/** 確保該語言的字典已在 i18next store(重複呼叫共用同一個載入;已載入 = 立即完成)。 */
export function ensureLanguageLoaded(lng: string | undefined | null): Promise<void> {
  const target = lazyLanguageOf(lng);
  if (!target) return Promise.resolve();
  if (i18n.hasResourceBundle(target, 'common')) return Promise.resolve();
  const inflight = pendingLoads.get(target);
  if (inflight) return inflight;
  // 換版後的舊分頁載不到舊 chunk → 重載一次拿新版;重載前先記下這個語言,重載後的開機
  // 偵測才會接著用它(否則使用者按了「English」,重載完還是繁中)。
  const p = importWithReload(`resources.${target}`, LAZY_LANGUAGE_LOADERS[target], () =>
    rememberLanguage(target),
  )
    .then((mod) => {
      for (const [ns, data] of Object.entries(collectNamespaces(mod.default))) {
        i18n.addResourceBundle(target, ns, data, true, true);
      }
    })
    .finally(() => {
      pendingLoads.delete(target);
    });
  pendingLoads.set(target, p);
  return p;
}

/** 與 LanguageDetector 的 `caches: ['localStorage']` 同一把鑰匙(`hai.lang`)。 */
function rememberLanguage(lng: string): void {
  try {
    localStorage.setItem('hai.lang', lng);
  } catch {
    // 隱私模式 / storage 不可用:重載後照偵測順序走,最差就是停在原本的語言
  }
}

/** 切換介面語言的唯一入口:先載字典,再切(切完 `resolvedLanguage` 才會是新語言)。 */
export async function changeAppLanguage(lng: string): Promise<void> {
  await ensureLanguageLoaded(lng);
  await i18n.changeLanguage(lng);
}

/**
 * R413 — `<html lang>` 跟著**實際顯示**的語言(`resolvedLanguage`;字典沒載到而退回繁中時就標繁中)。
 * 原本 index.html 寫死 `zh-TW`、從沒有人更新 → en / ja 使用者的讀屏用中文語音念英文 / 日文
 * (WCAG 3.1.1),瀏覽器的斷字(`hyphens: auto`)與翻譯提示也照繁中判斷。
 * 要在 init 之前掛上,init 當下那一次 languageChanged 才收得到。
 */
i18n.on('languageChanged', () => {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = i18n.resolvedLanguage ?? 'zh-TW';
});

const initDone = i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // R413 — 只帶 zh-TW;en / ja 由 `ensureLanguageLoaded` 補進 store(不設 backend)。
    partialBundledLanguages: true,
    fallbackLng: 'zh-TW',
    supportedLngs: ['zh-TW', 'en', 'ja'],
    defaultNS: 'common',
    // R281 — `ns` 從 `resources` 派生,不再手抄一份清單。
    //
    // 手抄那份漂移過兩次:`feedback`(R265)與 `redeem`(R276)加進 `resources` 卻沒
    // 進 `ns`。inline resources 之下 i18next 仍解得出 key(資源本來就在 store 裡),
    // 所以症狀是零 —— 正因為零徵兆才會連漏兩輪。派生之後這一類漂移結構上不存在。
    ns: Object.keys(resources['zh-TW']),
    interpolation: {
      escapeValue: false, // React 已 escape,不需再跑一次
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'hai.lang',
    },
  });

/**
 * 開機時偵測到 en / ja:等字典載入,再重新套用一次語言(init 當下還沒有字典,
 * `resolvedLanguage` 會落在 fallback zh-TW)。
 *
 * ⚠️ 載入期間語言已被換掉(使用者切了語言 / 測試 setup 釘回 zh-TW)→ **不可以**用開機偵測到的
 * 語言蓋回去。少了這道:jsdom 的 `navigator.language` 是 en-US,測試檔一 import 就在背景載英文,
 * 載完把 setup 釘好的繁中翻成英文 → 幾秒後的測試 toast 全變英文(R413 實際踩到,時序型、
 * 只在整檔跑時紅)。
 */
export async function finishBootLanguage(lng: string): Promise<void> {
  if (!lazyLanguageOf(lng)) return;
  await ensureLanguageLoaded(lng);
  if (i18n.language !== lng) return;
  await i18n.changeLanguage(lng);
}

/**
 * 開機語言就緒(`main.tsx` 等它才第一次 render)。zh-TW 使用者:init 完成即 resolve。
 * **永不 reject**(載入失敗照常 render,文字退回繁中)。
 */
export const i18nReady: Promise<void> = initDone
  .then(() => finishBootLanguage(i18n.language))
  .catch((err: unknown) => {
    console.error('[i18n] failed to load language resources; falling back to zh-TW', err);
  });

export default i18n;
